define((require) => {
    "use strict";

    const _               = require("underscore");
    const EventDispatcher = require("shared/util/EventDispatcher");
    const SyncManager     = require("shared/services/sync/SyncManager");
    const Syncer          = require("shared/services/sync/Syncer");
    const Preconditions   = require("shared/util/Preconditions");

    /**
     * Abstract collection for loading data in chunks aka lazy loading.
     *
     * Not a <tt>Backbone.Collection</tt> but mimics (roughly) the same semantics for consistency.
     *
     * @class {ChunkingCollection}
     * @see  ContentCollection
     */
    const ChunkingCollection = EventDispatcher.extend({

        /**
         * The model constructor or a <tt>Map</tt> of types to models, e.g. <tt>types.set(ExperienceModel.type, ExperienceModel)</tt>.
         *
         * @see  #_parseJSON
         * @see  ContentCollection#model
         * @type {Function|Map.<String, Model>}
         */
        model: null,

        /**
         * @type {Function}
         */
        url: null,

        /**
         * @type {Number}
         */
        limit: 20,

        /**
         * @type {Number}
         */
        offset: 0,

        /**
         * A reference to the current query.
         *
         * @type {Query}
         */
        _query: null,

        /**
         * @type {Number}
         * A sequence number assigned to each sync request as to only update this collection when sequence
         * number match when results are in, e.g. to discard results for "fo" when the user has entered "foo"
         * in the meantime; the first request is likely to finish after the last one as the criteria is more
         * restrictive. This is needed because different queues may back this collection instance accross time,
         * e.g. if the user is slower to enter a new letter than the debounce set.
         */
        _sequence: 0,

        /**
         * @type {Boolean}
         * True if this collection is tagged to be reset between sync requests, false otherwise.
         */
        _reset: false,

        /**
         *
         * @type {Boolean}
         */
        _hasMore: false,

        /**
         *
         * @type {Model[]}
         */
        _collection: null,

        /**
         * @type {Model[]}
         */
        _latest: null,

        /**
         * @type {Map.<String, Model>}
         */
        _idToModel: null,

        /**
         * Constructor.
         *
         * @param {Object} options The collection options:
         *                         @cfg {Function|String} url The endpoint URL, as a function returning the URL or the raw string URL.
         *                         @cfg {Number} limit The chunk size, defaults to 20. If -1 is defined, no limit will be used, i.e. all entries will be returned.
         */
        constructor(options) {
            this.options = options || {};
            this.url = this.options.url || this.url;
            this.limit = _.isNumber(this.options.limit) ? this.options.limit : this.limit;
            this._collection = [];
            this._idToModel = new Map();
        },

        /**
         * Resets the state of the collection and executes the specified query.
         *
         * @param  {ChunkingCollectionQuery} query  The query to execute.
         * @return {Promise}                        A promise that resolves when the query has been successfully executed.
         */
        query(query) {
            return this.reset()._fetch(query);
        },

        /**
         * Fetches the next chuck.
         *
         * @return {Promise} Resolves when the chuck has been fetched.
         */
        next() {
            Preconditions.checkState(this.hasMore());

            this.offset += this.limit;

            return this._fetch(this._query);
        },

        currentQuery() {
            return this._query;
        },

        /**
         * Whether more chucks can be fetched.
         *
         * @return {Boolean} <tt>true</tt> if more chucks can be fetched, <tt>false</tt> if not.
         */
        hasMore() {
            return this._hasMore;
        },

        each(callback) {
            this._collection.forEach(callback);
        },

        /**
         * Returns the latest chucks, as an array of models.
         *
         * @return {Model[]} The latest chunks.
         *
         * @see  #model
         */
        latest() {
            return _.clone(this._latest);
        },

        get(id) {
            return this._idToModel.get(id);
        },

        has(id) {
            return this._idToModel.has(id);
        },

        at(index) {
            return this._collection[index];
        },

        /**
         * Resets the current chunking collection.
         *
         * @param {Model[]} models An array of Backbone models which, if provided, will reset the chunking collection value.
         * @returns {ChunkingCollection}
         */
        reset(models) {
            if (models) {
                this._collection = models;
                this._latest = models;
                this._idToModel.clear();

                models.forEach((model) => {
                    this._idToModel.set(model.get("id"), model);
                });
                this._hasMore = this._collection.length === this.limit;
            } else {
                this._collection = [];
                this._latest = [];
                this._idToModel.clear();
            }

            this.offset = 0;
            this._reset = false;
            this.trigger("reset");
            return this;
        },

        models() {
            return this._collection;
        },

        length() {
            return this._collection.length;
        },

        isEmpty() {
            return this.length() === 0;
        },

        isPending() {
            return !Syncer.State.complete(this);
        },

        toJSON() {
            return this._collection.map((model) => (model.toJSON()));
        },

        add(model, index) {
            const models = Array.isArray(model) ? model : [model];

            this._add(models, index);
        },

        remove(model) {
            const ids = [];  // supposed to be only one `id`, but lets keep this method more generic just in case...
            this._idToModel.forEach((value, id) => {
                if (value === model) {
                    ids.push(id);
                }
            });
            ids.forEach((id) => this._idToModel.delete(id));

            const index = this._collection.indexOf(model);
            if (index >= 0) {
                this._collection.splice(index, 1);

                this.stopListening(model);

                this.trigger("remove", model, index);
            }
        },

        /**
         * Returns a user-friendly distinguished name for this collection.
         * Typically used to report errors.
         * Collections derived from this one may override `_distinguishedName()` method to have other name in logs.
         *
         * @returns {String}
         */
        distinguishedName() {
            let name = this._distinguishedName();

            // Default name?
            if (!name) {
                name = this.__typeName || "resource";
            }

            return name;
        },

        _fetch(query) {
            // Save a reference to the latest query, might be updated when the user enters letters in rapid
            // succession...
            this._query = query;

            // The request sent is not immutable as the same endpoint can describe different queries and thus results
            // and we want the queue promise returned, not the request promise, so we will wait for the user entering
            // more data like "fo" -> "foo"...
            return SyncManager.sync({
                method: "fetch",
                delay:  300,  // debounce until the user pauses entering a criteria, e.g. wait for "fo" -> "foo" in rapid succession
                fn: {
                    url: this._url(),
                    dataType: "json",
                    data: (request) => {
                        request.sequence = ++this._sequence;
                        // Generate the query at sync time, not at enqueue time. Also append "limit" and "offset" to
                        // the query request - we check for limit being falsey as it could potentially be set to -1
                        // (hint: ContentCollection#query).
                        return _.extend(this._query.querify(), this.limit > 0 ? {limit: this.limit, offset: this.offset} : {});
                    }
                },
                resultHandler: (json, request) => {
                    // Ignore stale queries based on sequence number, not on a changed query. The query is allowed
                    // to change while pending, e.g. user rapidly pressing Backspace or Delete, but the sequence
                    // numbers will not.
                    if (this._sequence !== request.sequence) {
                        return;
                    }

                    // If the queue at this point has additional queued entries, we now the results here will be
                    // overridden by those from such enqueued requests. Since this request is just about to complete,
                    // it will not be in the queue here. And, since we are using a queue promise, we will never return
                    // until the queue is empty. However, future queued requests may be cancelled before executed, but
                    // this is such a rare case we will ignore it (in the "if" above)...
                    if (this._reset) {
                        this.reset();
                    }

                    // Reset before processing *next* time around?
                    this._reset = request.queue.size() > 0;

                    // Parse json models into a temp collection...
                    const collection = this._parseJSON(json);

                    this._hasMore = collection.length === this.limit;

                    // Add new results to *this* collection...
                    this._add(collection);

                    // Explicitly mimic Backbone life cycle "sync" event, after "query:syncing", but before
                    // "query:synced", as triggered by the SyncManager...
                    this.trigger("sync");

                    return collection;
                },
                errorHandler: this._handleError

            }, this).promise();
        },

        _parseJSON(json) {
            const results = Array.isArray(json) ? json : [json];
            let factory;

            if (this.model instanceof Map) {
                factory = (entry) => {
                    const type = entry.type;
                    const Model = this.model.get(type);

                    return new Model(entry);
                };
            } else {
                factory = (entry) => {
                    const Model = this.model;

                    return new Model(entry);
                };
            }

            return results.map(factory);
        },

        /**
         * @returns {String} The string "alert"; never null.
         */
        _handleError(error) {
            error.collection = this;
            return "alert";
        },

        _add(collection, index) {
            this._latest = [];

            this.trigger("beforeAdd");

            let i = _.isNumber(index) ? index : this._collection.length - 1;

            collection.forEach((model) => {
                let id = model.get("id");

                // In case the model has no id (yet), create a temp id and wait for a proper id to arrive.
                if (!id) {
                    id = _.uniqueId("temp-");

                    this.listenToOnce(model, "change:id", (model, persistedId) => {
                        // In case the new ID is already present in the collection (e.g. the uploaded
                        // image was already present), remove the model again...
                        if (this.has(persistedId)) {
                            this.remove(model);
                        } else {
                            this._idToModel.set(persistedId, model);
                            this._idToModel.delete(id);
                        }
                    });
                }

                // Skip duplicates...
                if (!this.has(id)) {
                    this._collection.splice(++i, 0, model);
                    this._latest.push(model);
                    this._idToModel.set(id, model);

                    this.listenTo(model, "all", this._modelEventHandler);
                }
            });

            if (collection.length) {
                this.trigger("add", this.latest(), index);
            }
        },

        /**
         * @private
         *
         * Handles any, i.e. <tt>all</tt>, event(s) triggered from the contained model(s) - mimicking
         * the same collection behavior as the <tt>Backbone.Collection</tt>:<p>
         *
         * <q>Any event that is triggered on a model in a collection will also be triggered on the
         * collection directly, for convenience.</q>
         *
         * @param  {String} event      The event type.
         * @param  {Model} model       The model instance that triggered the event.
         * @param  {Backbone.Collection} collection A potential <tt>Backbone.Collection</tt>.
         */
        _modelEventHandler(event, model, collection) {
            if ((event === "add" || event === "remove") && collection !== this) {
                return;
            }

            if (event === "destroy" && this.options.autoRemoval) {
                this.remove(model);
            }

            this.trigger(event, model, collection);
        },

        /**
         * Sub-class specific hook to override the distinguished name of current collection.
         *
         * @return {String} The distinguished name; can be falsey, which will default to the class name of
         *                  this type, secondarily the string "resource".
         */
        _distinguishedName() {
            return null;
        },

        _url() {
            return _.isFunction(this.url) ? this.url(this.options) : this.url;
        }
    }, {

        /**
         * Convenience for executing a single query on the collection.
         *
         * By default, no limit/chunk size is defined.
         *
         * @param  {ChunkingCollectionQuery} query   The query to execute.
         * @param  {Object} options                  Options passed to the underlying collection instance.
         * @return {Promise}                         A promise that resolves when the query has been successfully executed.
         */
        findByQuery(query, options) {
            const collectionOptions = _.defaults(options || {}, {
                limit: -1 // i.e. no limit/chunk size...
            });

            const Collection = this;

            return new Collection(collectionOptions).query(query);
        }

    });

    return ChunkingCollection;
});



// WEBPACK FOOTER //
// ./src/editor/data/ChunkingCollection.js